import { FC, useCallback, useRef } from "react";

import type { ScalarValue } from "@tsmono/inspect-common/query";
import { PopOver } from "@tsmono/react/components";

import { ColumnFilterButton } from "./ColumnFilterButton";
import styles from "./ColumnFilterControl.module.css";
import { ColumnFilterEditor } from "./ColumnFilterEditor";
import { editorConditionProps } from "./editorConditionProps";
import type { FilterSpec, FilterType, UiOperator } from "./types";
import { useColumnFilterPopover } from "./useColumnFilterPopover";

interface ColumnFilterControlProps {
  columnId: string;
  filterType: FilterType;
  spec: FilterSpec | null;
  onChange: (spec: FilterSpec | null) => void;
  /** Override the operator choices (defaults to the full set for the type). */
  operators?: UiOperator[];
  /** Autocomplete suggestions for the filter value. */
  suggestions?: ScalarValue[];
  /** Called when the popover opens/closes (for fetching suggestions). */
  onOpenChange?: (columnId: string | null) => void;
  /**
   * Element to position the popover against instead of the funnel button.
   * Pass this when the funnel sits somewhere the popover shouldn't anchor —
   * e.g. a rotated header, where anchoring to the button would place the
   * popover over the headers; a bottom-of-cell anchor drops it below them
   * instead. When provided (even as null while a ref populates) the funnel is
   * not used as the reference.
   */
  anchorEl?: HTMLElement | null;
  /**
   * Popover placement. Defaults to `bottom-end` (right-aligned under the
   * funnel). Use `bottom-start` with a bottom-of-cell `anchorEl` to open the
   * popover down-and-right from the column, matching the AG grid.
   */
  placement?: "bottom-start" | "bottom-end";
}

export const ColumnFilterControl: FC<ColumnFilterControlProps> = ({
  columnId,
  filterType,
  spec,
  onChange,
  operators,
  suggestions = [],
  onOpenChange,
  anchorEl,
  placement = "bottom-end",
}) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const popover = useColumnFilterPopover({
    columnId,
    filterType,
    spec,
    onChange,
    operators,
  });
  const { isOpen, setIsOpen, operatorOptions, commitAndClose, cancelAndClose } =
    popover;

  const handlePopoverOpenChange = useCallback(
    (nextOpen: boolean) => {
      setIsOpen(nextOpen);
      onOpenChange?.(nextOpen ? columnId : null);
    },
    [setIsOpen, onOpenChange, columnId]
  );

  return (
    <div className={styles.headerActions}>
      <ColumnFilterButton
        ref={buttonRef}
        columnId={columnId}
        isActive={!!spec}
        onClick={(event) => {
          event.stopPropagation();
          handlePopoverOpenChange(!isOpen);
        }}
      />
      <PopOver
        id={`column-filter-${columnId}`}
        isOpen={isOpen}
        setIsOpen={handlePopoverOpenChange}
        // When an anchorEl is intended, use it (even if still null) rather
        // than the funnel button.
        // eslint-disable-next-line react-hooks/refs -- positionEl accepts null; PopOver/Popper handles this in effects and updates when ref is populated
        positionEl={anchorEl !== undefined ? anchorEl : buttonRef.current}
        placement={placement}
        showArrow={true}
        hoverDelay={-1}
        className={styles.filterPopover}
        closeOnMouseLeave={false}
        styles={{
          padding: "0.4rem",
          backgroundColor: "var(--bs-light)",
        }}
      >
        <ColumnFilterEditor
          columnId={columnId}
          filterType={filterType}
          operatorOptions={operatorOptions}
          {...editorConditionProps(popover)}
          onCommit={commitAndClose}
          onCancel={cancelAndClose}
          suggestions={suggestions}
        />
      </PopOver>
    </div>
  );
};
